import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import PageShell from './PageShell';
import { api } from '../lib/api';
import { lessonCodesToEtapeIds } from '../data/lessonCodeToEtapesMap';
import EtapeRemcCard, { findEtape } from '../components/EtapeRemcCard';
import {
  User, Calendar, Clock, MapPin, TrendingUp, AlertCircle, Lightbulb,
  CheckCircle2, Loader2, GraduationCap, ArrowRight, Printer,
} from 'lucide-react';

export default function SessionSheetPublicPage() {
  const { token } = useParams();
  const [sheet, setSheet] = useState(null);
  const [error, setError] = useState('');
  const [progression, setProgression] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const s = await api.publicSessionSheet(token);
        if (alive) setSheet(s);
      } catch (err) {
        if (alive) setError(err?.response?.data?.detail || 'Fiche introuvable ou expirée.');
      }
      try {
        const p = await api.publicSheetProgression(token);
        if (alive) setProgression(p);
      } catch { /* silent */ }
    })();
    return () => { alive = false; };
  }, [token]);

  // (Étapes de progression déduites via lessonCodesToEtapeIds)

  if (error) {
    return (
      <PageShell>
        <div className="max-w-2xl mx-auto p-12 text-center">
          <AlertCircle className="mx-auto text-red-400 mb-4" size={32} />
          <h2 className="font-serif-editorial text-white text-[26px]">Fiche introuvable</h2>
          <p className="text-white/60 mt-2 text-[14px]">{error}</p>
          <Link to="/" className="inline-block mt-6 h-11 px-5 rounded-full bg-red-600 hover:bg-red-500 text-white text-[13px] font-bold">Retour à l&apos;accueil</Link>
        </div>
      </PageShell>
    );
  }
  if (!sheet) {
    return (
      <PageShell>
        <div className="max-w-2xl mx-auto p-12 text-center text-white/50">
          <Loader2 className="animate-spin mx-auto mb-3" size={24} /> Chargement de la fiche…
        </div>
      </PageShell>
    );
  }

  const dateFr = sheet.date ? new Date(sheet.date).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }) : '';

  return (
    <PageShell>
      <section className="max-w-3xl mx-auto px-4 lg:px-6 py-8 sm:py-12" data-testid="sheet-public">
        <nav className="flex items-center gap-2 text-[12px] text-white/50 font-mono-spec mb-6 no-print">
          <Link to="/" className="hover:text-white">codevoiture.fr</Link>
          <span className="text-white/25">/</span>
          <span className="text-white/70">Fiche de séance</span>
        </nav>

        <div className="rounded-2xl border border-white/10 bg-gradient-to-br from-[#161618] to-[#0d0d0e] p-6 sm:p-8 relative overflow-hidden">
          <div className="absolute -top-8 -right-8 opacity-[0.06] pointer-events-none">
            <GraduationCap size={220} strokeWidth={1} />
          </div>
          <div className="relative">
            <div className="text-[10.5px] uppercase tracking-[0.22em] text-red-400 font-mono-spec font-bold">Fiche de séance conduite</div>
            <h1 className="mt-2 font-serif-editorial text-white text-[28px] sm:text-[34px] leading-tight">Bonjour {sheet.student_name}</h1>
            <p className="mt-2 text-white/65 text-[14px]">Voici le récapitulatif de ta séance avec <span className="text-white font-semibold">{sheet.teacher_name}</span>.</p>

            <div className="mt-5 flex flex-wrap gap-x-5 gap-y-2 text-[12.5px] text-white/70">
              {sheet.date && <span className="inline-flex items-center gap-1.5"><Calendar size={12} className="text-white/40" /> {dateFr}</span>}
              {sheet.duration_min && <span className="inline-flex items-center gap-1.5"><Clock size={12} className="text-white/40" /> {sheet.duration_min} min</span>}
              {sheet.lieu && <span className="inline-flex items-center gap-1.5"><MapPin size={12} className="text-white/40" /> {sheet.lieu}</span>}
              {sheet.teacher_code && <span className="inline-flex items-center gap-1.5 no-print"><User size={12} className="text-white/40" /> Code enseignant : <span className="font-mono-spec text-red-300">{sheet.teacher_code}</span></span>}
            </div>
          </div>
        </div>

        <div className="mt-6 space-y-4">
          {/* Jauge de progression cumulée */}
          {progression && progression.sessions_count > 0 && (
            <div className="rounded-2xl border border-emerald-500/30 bg-gradient-to-br from-emerald-950/30 to-[#0d0d0e] p-5 sm:p-6" data-testid="progression-gauge">
              <div className="flex items-baseline justify-between flex-wrap gap-2 mb-3">
                <div className="text-emerald-300 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold inline-flex items-center gap-1.5">
                  <TrendingUp size={11} /> Ta progression avec {sheet.teacher_name}
                </div>
                <span className="text-white/50 text-[11px] font-mono-spec">{progression.sessions_count} séance{progression.sessions_count > 1 ? 's' : ''} · {progression.themes_count} thème{progression.themes_count > 1 ? 's' : ''} abordé{progression.themes_count > 1 ? 's' : ''}</span>
              </div>
              <div className="flex items-baseline gap-2 mb-2">
                <span className="text-emerald-300 font-serif-editorial text-[38px] leading-none">{progression.lessons_done}</span>
                <span className="text-white/50 text-[15px]">/ {progression.lessons_total} leçons REMC travaillées</span>
              </div>
              <div className="mt-2 h-3 rounded-full bg-white/10 overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-emerald-500 via-emerald-400 to-emerald-300 rounded-full transition-all duration-1000 ease-out"
                  style={{ width: `${Math.min(100, Math.round((progression.lessons_done / progression.lessons_total) * 100))}%` }}
                />
              </div>
              <div className="mt-3 grid grid-cols-4 gap-2">
                {['C1', 'C2', 'C3', 'C4'].map((c) => {
                  const total = { C1: 9, C2: 7, C3: 9, C4: 7 }[c];
                  const done = (progression.lesson_codes_done || []).filter((lc) => lc.startsWith(c)).length;
                  const pct = Math.round((done / total) * 100);
                  return (
                    <div key={c} className="rounded-lg border border-white/10 bg-[#0a0a0b] p-2.5">
                      <div className="text-red-300 text-[10px] font-mono-spec font-bold">{c}</div>
                      <div className="mt-0.5 text-white text-[13px] font-semibold">{done}/{total}</div>
                      <div className="mt-1 h-1 rounded-full bg-white/10 overflow-hidden">
                        <div className="h-full bg-emerald-400 rounded-full transition-all duration-1000" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
              <p className="mt-3 text-white/55 text-[12px] leading-relaxed">
                Chaque leçon REMC travaillée avec ton moniteur est comptée ici. Continue, tu es sur la bonne voie ! 🚀
              </p>
            </div>
          )}
          {sheet.competences && sheet.competences.length > 0 && (
            <div className="rounded-xl border border-white/10 bg-[#0d0d0e] p-5">
              <div className="text-red-400 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold mb-3">Compétences REMC travaillées</div>
              <div className="flex flex-wrap gap-2">
                {sheet.competences.map((c) => (
                  <span key={c} className="px-3 py-1 rounded-full border border-red-500/40 bg-red-500/10 text-red-200 text-[12px] font-semibold">{c}</span>
                ))}
              </div>
            </div>
          )}

          {sheet.themes_travailles && sheet.themes_travailles.length > 0 && (
            <div className="rounded-xl border border-white/10 bg-[#0d0d0e] p-5">
              <div className="text-red-400 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold mb-3">Thèmes abordés</div>
              <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-1.5 text-white/85 text-[13.5px]">
                {sheet.themes_travailles.map((t, i) => <li key={i} className="flex items-start gap-2"><span className="text-red-400 shrink-0">•</span> {t}</li>)}
              </ul>
            </div>
          )}

          {sheet.points_forts && (
            <div className="rounded-xl border border-emerald-500/30 bg-emerald-950/15 p-5">
              <div className="text-emerald-300 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold mb-2 inline-flex items-center gap-1.5"><CheckCircle2 size={12} /> Points forts</div>
              <p className="text-white/85 text-[14px] leading-relaxed whitespace-pre-wrap">{sheet.points_forts}</p>
            </div>
          )}

          {sheet.points_a_travailler && (
            <div className="rounded-xl border border-amber-500/30 bg-amber-950/15 p-5">
              <div className="text-amber-300 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold mb-2 inline-flex items-center gap-1.5"><AlertCircle size={12} /> À travailler</div>
              <p className="text-white/85 text-[14px] leading-relaxed whitespace-pre-wrap">{sheet.points_a_travailler}</p>
            </div>
          )}

          {sheet.conseils && (
            <div className="rounded-xl border border-sky-500/30 bg-sky-950/15 p-5">
              <div className="text-sky-300 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold mb-2 inline-flex items-center gap-1.5"><Lightbulb size={12} /> Conseils personnalisés</div>
              <p className="text-white/85 text-[14px] leading-relaxed whitespace-pre-wrap">{sheet.conseils}</p>
            </div>
          )}

          {sheet.prochaine_seance && (
            <div className="rounded-xl border border-white/10 bg-[#0d0d0e] p-5">
              <div className="text-white/60 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold mb-2 inline-flex items-center gap-1.5"><TrendingUp size={12} /> Prochaine séance</div>
              <p className="text-white/90 text-[14px]">{sheet.prochaine_seance}</p>
            </div>
          )}
        </div>

        {/* Étapes de progression travaillées (basé sur lesson_codes → étapes) */}
        {sheet.lesson_codes && sheet.lesson_codes.length > 0 && (() => {
          const etapeIds = (sheet.steps && sheet.steps.length > 0) ? sheet.steps : lessonCodesToEtapeIds(sheet.lesson_codes);
          const etapes = etapeIds.map(findEtape).filter(Boolean).sort((a, b) => {
            const order = { Mécanique: 0, Observation: 1, 'Préparation permis': 2 };
            const pa = order[a.phase] ?? 9;
            const pb = order[b.phase] ?? 9;
            if (pa !== pb) return pa - pb;
            return a.id.localeCompare(b.id, undefined, { numeric: true });
          });
          if (etapes.length === 0) return null;
          return (
            <div className="mt-8" data-testid="sheet-etapes-block">
              <div className="text-[10.5px] uppercase tracking-[0.22em] text-red-400 font-mono-spec font-bold inline-flex items-center gap-1.5"><TrendingUp size={12} /> Étapes travaillées aujourd&apos;hui</div>
              <h2 className="mt-2 font-serif-editorial text-white text-[24px] sm:text-[28px] leading-tight">
                {etapes.length} étape{etapes.length > 1 ? 's' : ''} de ta progression
              </h2>
              <p className="mt-1.5 text-white/60 text-[13px]">
                Clique sur chaque étape pour voir <span className="text-emerald-300 font-semibold">pourquoi</span>, <span className="text-sky-300 font-semibold">comment</span> et <span className="text-red-300 font-semibold">les risques</span>. Retrouve tout dans ta <Link to="/cours/progression" className="underline text-white hover:text-red-300">progression complète</Link>.
              </p>

              <div className="mt-5 space-y-2.5">
                {etapes.map((etape) => (
                  <EtapeRemcCard key={etape.id} etapeId={etape.id} defaultOpen={false} />
                ))}
              </div>

              <div className="mt-5 flex flex-wrap gap-2">
                <Link
                  to="/cours/progression"
                  data-testid="sheet-open-progression"
                  className="inline-flex items-center gap-1.5 h-11 px-5 rounded-full bg-emerald-500 hover:bg-emerald-400 text-emerald-950 text-[13px] font-bold shadow-lg shadow-emerald-950/40"
                >
                  <TrendingUp size={13} /> Ouvrir Ma progression <ArrowRight size={13} />
                </Link>
              </div>
            </div>
          );
        })()}

        <div className="hidden" data-marker="cta-signup"></div>

        <div className="mt-8 rounded-xl border border-white/10 bg-[#0d0d0e] p-4 no-print" data-testid="sheet-cta-signup">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-full bg-white/5 border border-white/10 flex items-center justify-center shrink-0">
              <Lightbulb size={14} className="text-white/50" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-white/85 text-[13px] font-semibold">Bonus (optionnel) · Retrouve toutes tes fiches</div>
              <p className="mt-0.5 text-white/50 text-[12px] leading-relaxed">Si tu veux, tu peux créer un compte gratuit pour retrouver l&apos;historique de toutes tes fiches et suivre ta progression complète. Pas obligatoire — ta fiche du jour est déjà ici.</p>
              <div className="mt-2.5 flex flex-wrap gap-2">
                <Link to={sheet.teacher_code ? `/rejoindre?code=${sheet.teacher_code}` : '/'} className="h-8 px-3 rounded-full border border-white/15 text-white/70 hover:text-white hover:border-white/35 text-[11.5px] font-semibold inline-flex items-center gap-1.5">
                  Créer un compte
                </Link>
                <button onClick={() => window.print()} className="h-8 px-3 rounded-full border border-white/15 text-white/70 hover:text-white hover:border-white/35 text-[11.5px] font-semibold inline-flex items-center gap-1.5">
                  <Printer size={11} /> Imprimer / PDF
                </button>
              </div>
            </div>
          </div>
        </div>

        <p className="mt-6 text-center text-white/35 text-[11px] font-mono-spec">
          Fiche générée par {sheet.teacher_name} · codevoiture.fr · Fiche consultée {sheet.view_count} fois
        </p>
      </section>
    </PageShell>
  );
}
